import * as React from 'react'

/**
 * CdnImage — emits provider-agnostic image URLs without depending on a
 * image URLs using the `width=` query parameter (understood by Bunny Optimizer).
 *
 * - Generates a srcset across standard widths when `sizes` is provided.
 * - Supports lazy loading via `loading="lazy"` and an optional low-quality placeholder.
 * - Accepts `htmlAttributes` for compatibility with the legacy API surface.
 */

const DEFAULT_SRCSET_WIDTHS = [100, 200, 300, 400, 600, 800, 1000, 1200, 1600, 2000]

interface CdnImageProps {
  src: string
  width?: number
  height?: number
  sizes?: string
  className?: string
  alt?: string
  htmlAttributes?: React.ImgHTMLAttributes<HTMLImageElement>
  /** Widths to include in srcset. Defaults to DEFAULT_SRCSET_WIDTHS. */
  srcsetWidths?: number[]
  /** If true, disables srcset generation (single URL). */
  disableSrcSet?: boolean
  /**
   * If true, emits the image URL as data-src/data-srcset (with a placeholder
   * in src) so lazysizes can swap it in on intersection. Add a "lazyload"
   * className via `className` to trigger lazysizes.
   */
  lazyLoad?: boolean
  /** Placeholder src to use while lazy-loading. */
  placeholder?: string
}

const applyDimensions = (src: string, width: number, height?: number): string => {
  try {
    const url = new URL(src)
    url.searchParams.set('width', String(width))
    if (height) url.searchParams.set('height', String(height))
    return url.toString()
  } catch {
    // Fallback for relative URLs or malformed input
    const separator = src.includes('?') ? '&' : '?'
    const heightParam = height ? `&height=${height}` : ''
    return `${src}${separator}width=${width}${heightParam}`
  }
}

// For each srcset entry we scale height proportionally to match the
// requested aspect ratio (derived from the width/height props).
const buildSrcSet = (
  src: string,
  widths: number[],
  baseWidth?: number,
  baseHeight?: number
): string => {
  const aspectRatio = baseWidth && baseHeight ? baseHeight / baseWidth : undefined
  return widths
    .map((w) => {
      const h = aspectRatio ? Math.round(w * aspectRatio) : undefined
      return `${applyDimensions(src, w, h)} ${w}w`
    })
    .join(', ')
}

const CdnImage: React.FC<CdnImageProps> = ({
  src,
  width,
  height,
  sizes,
  className,
  alt,
  htmlAttributes = {},
  srcsetWidths = DEFAULT_SRCSET_WIDTHS,
  disableSrcSet = false,
  lazyLoad = false,
  placeholder
}) => {
  const displaySrc = width ? applyDimensions(src, width, height) : src
  const srcSet =
    sizes && !disableSrcSet ? buildSrcSet(src, srcsetWidths, width, height) : undefined

  // When lazy loading via lazysizes, the real URL goes in data-src / data-srcset
  // and the visible src is a lightweight placeholder until intersection.
  const lazyAttrs = lazyLoad
    ? {
        src: placeholder ?? displaySrc,
        'data-src': displaySrc,
        'data-srcset': srcSet,
        'data-sizes': sizes
      }
    : {
        src: displaySrc,
        srcSet,
        sizes
      }

  return (
    <img
      width={width}
      height={height}
      className={className}
      alt={alt ?? ''}
      {...lazyAttrs}
      {...htmlAttributes}
    />
  )
}

export default CdnImage
